import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { CreatePasswordStep } from '@/components/signup/CreatePasswordStep';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const CreatePasswordPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();

  // Redirect if not authenticated or doesn't need password reset
  React.useEffect(() => {
    if (!loading) {
      if (!user || !user.email_confirmed_at) {
        toast({
          title: "Verification required",
          description: "Please verify your email address first.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Only allow access if user needs password reset (from signup flow)
      if (!user.user_metadata?.requires_password_reset) {
        toast({
          title: "Access denied",
          description: "This page is only for new account setup.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }
    }
  }, [user, loading, navigate, toast]);

  const handlePasswordCreated = async () => {
    try {
      // Update user metadata to clear password reset flag
      const { error } = await supabase.auth.updateUser({
        data: { 
          requires_password_reset: false,
          has_password: true 
        }
      });

      if (error) {
        console.error('Error updating user metadata:', error);
      }

      toast({
        title: "Password created!",
        description: "Now let's complete your profile.",
      });

      // Move to profile completion
      navigate('/onboarding/profile');
    } catch (error) {
      console.error('Password creation completion error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !user.email_confirmed_at || !user.user_metadata?.requires_password_reset) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 bg-primary/10 p-3 rounded-full w-fit">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            Create a password
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Secure your Safurra account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CreatePasswordStep
            onPasswordCreated={handlePasswordCreated}
            isLoading={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePasswordPage;